import { Component, ErrorInfo, ReactNode } from 'react'

import { NotFound } from '@fe-toolkit/web-components'

import MainLayout from '@/layouts/Main'
import logger from '@/lib/logger'

type ErrorBoundaryProps = {
  children: ReactNode
  // Changing this while hasError is true clears the error and re-renders children -
  // e.g. pass the current route so navigating away recovers, without remounting children on every navigation.
  resetKey?: string
}

type ErrorBoundaryState = {
  hasError: boolean
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Uncaught error:', {
      error,
      componentStack: errorInfo.componentStack ?? undefined,
    })
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <MainLayout>
          <NotFound />
        </MainLayout>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
